/* eslint-disable max-len,@typescript-eslint/member-ordering */
import {
  inject,
  Injectable,
  OnDestroy,
} from '@angular/core';
import {
  AnnotationIcon,
  AnnotJson,
  DeviceRect, FoxitModule,
  isMarkup,
  PDFDoc,
  PDFPage,
  PDFPageRender,
  PDFRect,
  PDFUI, PDFUIOptions, PDFViewCtrl,
} from 'webcommon/lazy-load-commonjs/foxit';

import {
  EditAttachmentDto, FoxitLicenseDto,
  getHttpError, isModelStateError, ProviderSignatureDto, retryAndRethrow,
} from 'webcommon/shared';

import {
  AttachmentRepository, ProviderRepository,
} from 'webcommon/webapi';

import { forkJoin, from, fromEvent, Observable, of, throwError } from 'rxjs';
import { concatMap, filter, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { FoxitPdfRendererBase } from './FoxitPdfRendererBase';
import { IPdfRenderer } from './IPdfRenderer';
import moment from 'moment';
import { WebCommonStorageRepository } from 'webcommon/legacy-common';

@Injectable()
export class FoxitSignPdfRenderer extends FoxitPdfRendererBase implements IPdfRenderer, OnDestroy {
  override readonly pdfUI = this.createPDFUI();

  protected override getNewTemplate(): PDFUIOptions['template'] {
    return `
<webpdf>
  <toolbar name="toolbar" class="fv__ui-toolbar-scrollable">
    <tabs name="toolbar-tabs">
      <tab title="toolbar.tabs.home.title" name="home-tab">
        <group-list name="home-toolbar-group-list">
          <group name="home-tab-group-hand" retain-count="10">
            <hand-button></hand-button>
            <selection-button></selection-button>
            <create-typewriter-button></create-typewriter-button>
            <xbutton name="save-button" @tooltip tooltip-title="Save Document" icon-class="ap-fv__icon-toolbar-save">Save</xbutton>
          </group>
          <group name="comment-tab-group-stamp" retain-count="5">
            <single-page-button></single-page-button>
            <continuous-page-button></continuous-page-button>
            <stamp-dropdown></stamp-dropdown>
          </group>
        </group-list>
      </tab>
    </tabs>
  </toolbar>
  <viewer></viewer>
  <template name="template-container">
    <! -- contextmenus -->
    <markup-contextmenu name="fv--stamp-contextmenu"></markup-contextmenu>
  </template>
</webpdf>
    `;
  }

  protected override getOptions(
    foxitModule: FoxitModule,
    foxitSetup: FoxitLicenseDto,
    element: HTMLElement,
  ): PDFUIOptions {
    const defaultOptions = super.getOptions(foxitModule, foxitSetup, element);
    defaultOptions.fragments = [
      {
        config: {
          callback: this.saveAttachment.bind(this),
          tooltip: {
            title: 'Save PDF',
          },
        },
        target: 'save-button',
      },
      {
        target: 'fv--stamp-contextmenu>fv--contextmenu-item-reply',
        // remove from stamp menu
        action: foxitModule.UIConsts.FRAGMENT_ACTION.REMOVE,
      },
    ];
    return defaultOptions;
  }

  attachmentsToSave = new Map<string, string>();
  isDirty = false;
  pageCount = 0;
  pageIndex = 0;
  currentAttachmentUid?: string;
  providerSignatureWidth = 0;
  providerSignatureHeight = 0;
  providerName: string;
  defaultSignatureFontSize = 6;

  private readonly attachmentRepository = inject(AttachmentRepository);
  private readonly providerRepository = inject(ProviderRepository);
  private readonly webcommonStorageRepository = inject(WebCommonStorageRepository);

  protected override createPDFUI(): PDFUI {
    this.isDirty = false;

    const pdfUI = super.createPDFUI();

    pdfUI.eventEmitter.on('page-number-change', (newPageNumber) => {
      this.pageIndex = newPageNumber - 1;
    });
    pdfUI.getPDFViewer().then((viewer) => {
      viewer.eventEmitter.on('annotation-add', async (annotAdd) => {
        if ((annotAdd[0].getTitle() === 'Foxit Web')
          && (annotAdd[0].getType() === 'stamp')
        ) {
          const stampId = annotAdd[0].getUniqueID();
          const pageRender = await pdfUI.getPDFPageRender(this.pageIndex);
          if (!pageRender) {
            return;
          }
          const page = await pageRender.getPDFPage();
          const signatureRect = annotAdd[0].getRect();
          await page.removeAnnotById(stampId);
          await this.addProviderSignatureWithTimestampMessage(page, signatureRect, pageRender);
        }
        this.isDirty = true;
      });
    });
    pdfUI.getPDFViewer().then((viewer) => {
      viewer.eventEmitter.on('annotation-appearance-updated', async (annotUpdate) => {
        const firstAnnot = annotUpdate[0];
        if (isMarkup(firstAnnot)) {
          if (firstAnnot.getTitle() === 'ProviderSignature') {
            const groupElements = firstAnnot.getGroupElements();
            const signatureAnnot = groupElements[0];
            const datetimeStampAnnot = groupElements[1];
            const signatureRect = signatureAnnot.getRect();
            const datetimeStampAnnotRect = datetimeStampAnnot.getRect();
            datetimeStampAnnotRect.top = signatureRect.bottom - 1;
            datetimeStampAnnotRect.left = signatureRect.left;
            datetimeStampAnnotRect.right = signatureRect.right;
            datetimeStampAnnotRect.bottom = signatureRect.bottom - 31;
            const success = await datetimeStampAnnot.setRect(datetimeStampAnnotRect);
            if (success) {
              const pageRender = await pdfUI.getPDFPageRender(this.pageIndex);
              if (!pageRender) {
                return;
              }
              const component = pageRender.getAnnotRender(datetimeStampAnnot.getUniqueID())?.getComponent();
              component?.active();
            }
          }
        }
      });
    });
    pdfUI.registerMatchRule((annot, ParentClass) => {
      if ('stamp' === annot.getType()) {
        return class CustomMarkupAnnotClass extends ParentClass {
          constructor(...args: any[]) {
            super(...args);
          }
          onDoubleTap: PDFViewCtrl.MarkupAnnotComponent['onDoubleTap'] = () => {
            return false; // to prevent default behavior
          };
          showReplyDialog: PDFViewCtrl.MarkupAnnotComponent['showReplyDialog'] = () => {
            return false;
          };
        };
      }
      return ParentClass;
    });

    pdfUI.getPDFViewer().then((viewer) => {
      viewer.setDefaultAnnotConfig((type) => {
        const config: Partial<AnnotJson> = {};
        config.borderInfo = {};
        config.defaultAppearance = {};
        switch (type) {
          case 'freetext':
            config.color = 'ffffff';
            config.opacity = 1;
            config.fillColor = 0x000000;
            config.borderInfo.width = 0;
            config.intent = 'FreeTextTypewriter';
            config.defaultAppearance.textColor = 0;
            config.defaultAppearance.textSize = 14;
            break;
        }
        return config;
      });
    });
    return pdfUI;
  }

  override async openPDFData(base64String: string): Promise<void> {
    this.pageIndex = 0;
    await super.openPDFData(base64String);
    await this.removeDefaultStamps();
    await this.setProviderStamp();
  }

  async setProviderStamp(): Promise<void> {
    const providerSignatureData = await this.getProviderSignatureBase64();
    if (!providerSignatureData) {
      return;
    }

    this.providerName = providerSignatureData.ProviderName;
    const src = 'data:image/bmp;base64,' + providerSignatureData.Signature;
    const i = await this.browserService.loadImage(src);
    const icon: AnnotationIcon = {
      annotType: 'stamp',
      category: 'Provider Signature',
      fileType: 'bmp',
      height: i.height,
      name: 'Signature Icon',
      url: i.src,
      width: i.width,
    };
    this.providerSignatureHeight = i.height;
    this.providerSignatureWidth = i.width;
    await this.pdfUI.pdfViewer.addAnnotationIcon(icon);
  }

  async removeDefaultStamps(): Promise<void> {
    // this.pdfUI.removeAnnotationIcon('stamp', null, null);
    await this.pdfUI.pdfViewer.removeAnnotationIcon('stamp', null, null);
    // this.pdfUI.pdfViewer.removeAnnotationIcon('stamp', 'Dynamic Stamps', 'Approved');
    // this.pdfUI.pdfViewer.removeAnnotationIcon('stamp', 'Dynamic Stamps', 'Revised');
    // this.pdfUI.pdfViewer.removeAnnotationIcon('stamp', 'Dynamic Stamps', 'Reviewed');
  }

  async pdfDocumentStream(pdfDoc: PDFDoc): Promise<Blob> {
    const bufferArray: BlobPart[] = [];
    await pdfDoc.getStream(({ arrayBuffer }) => {
      bufferArray.push(arrayBuffer);
    });
    return new Blob(bufferArray, { type: 'application/pdf' });
  }

  async saveAttachment(): Promise<void> {
    const currentAttachmentUid = this.currentAttachmentUid;
    if (!currentAttachmentUid) {
      return;
    }

    const pdfDoc = await this.pdfUI.getCurrentPDFDoc();
    if (!pdfDoc) {
      return;
    }

    await pdfDoc.flatten(0);
    const pdfBlob = await this.pdfDocumentStream(pdfDoc);
    const pdfBase64 = await this.browserService.blobToBase64(pdfBlob);
    const editedAttachment: EditAttachmentDto = {
      Data: pdfBase64,
      AttachmentUid: currentAttachmentUid,
    };
    await this.attachmentRepository.setAttachmentContent(
      (editedAttachment.AttachmentUid).toString(),
      editedAttachment,
    ).toPromise();
  }

  setAttachment(id: string) {
    this.currentAttachmentUid = id;
  }

  // returns null if no ProviderSignature
  async getProviderSignatureBase64(): Promise<ProviderSignatureDto | null> {
    return await this.providerRepository.GetCurrentProviderSigningSignature().toPromise().catch((error: unknown) => {
      if (getHttpError(error, isModelStateError)) {
        // if it's a ModelStateError, then that should just mean there is no signature, which is a valid state
        return null;
      }
      // else, rethrow it
      throw error;
    });
  }

  // setup behavior to drag down-right for provider signature, and up right to add the date
  setupDragBehavior(): void {
    const mousedown$ = fromEvent<MouseEvent>(this.element, 'mousedown');
    fromEvent<MouseEvent>(this.element, 'mouseup').pipe(
      // get the latest mousedown event for each mouseup
      withLatestFrom(mousedown$),
      // wait isRegularPageDrag() to emit,
      // and because this is switchMap, it will properly unsubscribe to the last call and move on with a new
      // call to isRegularPageDrag(), if a new mouseup event comes thru.
      // This will support the behavior we currently want, which is that if there has been no
      // future event (select-text, pdfui-end-scrolling-page) after the mouse events to confirm that this could be a valid drag event,
      // then cancel it.
      switchMap(([endEvent, startEvent]) => {
        return this.isRegularPageDrag().pipe(
          map((isRegularDrag) => {
            return {
              isRegularDrag,
              startEvent,
              endEvent,
            };
          }),
        );
      }),
      // filter out invalid drag events
      filter((value) => value.isRegularDrag),
      concatMap((value) => {
        // the set of events is now valid to process
        return this.handleDrag(value.startEvent, value.endEvent);
      }),
      retryAndRethrow(),
      this.takeUntilDestroy(),
    ).subscribe();
  }

  async handleDrag(startEvent: MouseEvent, endEvent: MouseEvent): Promise<void> {
    let pageScale = 1;
    const pageRender = await this.pdfUI.getPDFPageRender(this.pageIndex);
    if (!pageRender) {
      return;
    }

    const eHandler = pageRender.getHandlerDOM();
    const pageRect = eHandler.getBoundingClientRect();
    // TODO: is this supposed to be using offsetX/offsetY or clientX/clientY, or a mix of both
    // this logic seems like it could be wrong; it was introduced in 73f975d
    const startPoints = [startEvent.offsetX, startEvent.offsetY] as const;
    const endPoints = [endEvent.clientX, endEvent.clientY] as const;
    const rect = this.getRect(startPoints, pageRect, endPoints);
    pageScale = pageRender.getScale();
    const page = await pageRender.getPDFPage();
    const reversedRect = page.reverseDeviceRect(rect, pageScale);
    if ((startEvent.clientY < endEvent.clientY) && (startEvent.clientX < endEvent.clientX)) {
      reversedRect.right = reversedRect.left + this.providerSignatureWidth;
      reversedRect.bottom = reversedRect.top - this.providerSignatureHeight;
      await this.addProviderSignatureWithTimestampMessage(page, reversedRect, pageRender);
    } else if ((startEvent.clientY > endEvent.clientY) && (startEvent.clientX < endEvent.clientX)) {
      await this.addDate(page, reversedRect, pageRender);
    }
  }

  // return true if this event is a drag on the page without it being for resizing or moving annotations,
  // or anything like that.
  // To see all events in foxit, prettify UIExtension.full.js,
  // and put logpoint right after "u.prototype.emit = function(e, t, n, o, i, a) {"
  // This is the emit call in eventemitter3
  isRegularPageDrag(): Observable<boolean> {
    return new Observable<boolean>((observer) => {
      // there could also be events that we listen to that definitely cancel these other events
      // and return false, if we deem that necessary
      const teardown1 = this.pdfUI.addViewerEventListener({
        'select-text': () => {
          // for STATE_HANDLER_SELECT_ANNOTATION, we expect a single select-text event after the drag
          teardown1();
          observer.next(true);
          observer.complete();
        },
        'pdfui-end-scrolling-page': () => {
          // for STATE_HANDLER_HAND, we expect a single pdfui-end-scrolling-page event after the drag
          teardown1();
          observer.next(true);
          observer.complete();
        },
      });
      return teardown1;
    });
  }

  async addProviderSignatureWithTimestampMessage(page: PDFPage, rect: PDFRect, pageRender: PDFPageRender): Promise<void> {
    if (!this.providerSignatureHeight) {
      // if there is no loaded signature, then don't try to add a non-existent signature to the document
      return;
    }

    const providerSignature: AnnotJson = {
      icon: 'Signature Icon',
      iconCategory: 'Provider Signature',
      opacity: 1,
      rect: {
        bottom: rect.bottom,
        left: rect.left,
        right: rect.right,
        top: rect.top,
      },
      rotate: page.getRotation(),
      title: 'ProviderSignature',
      type: 'stamp',
    };
    const timestampMessage: AnnotJson = {
      color: 'ffffff',
      contents: 'Electronically signed by ' + this.providerName + ' on ' + this.formatDate(true),
      'interior-color': 0x00000000,
      rect: {
        bottom: rect.bottom - 31,
        left: rect.left,
        right: rect.right,
        top: rect.bottom - 1,
      },
      rotate: page.getRotation(),
      subject: 'FreeTextTypeWriter',
      'text-color': 0x000000,
      title: 'ElectronicSignatureTimestamp',
      type: 'freetext',
      defaultAppearance: {
        textColor: 0,
        textSize: this.webcommonStorageRepository.getSiteSettings()?.AttachmentApprovalSettings?.SignatureFontSize || this.defaultSignatureFontSize,
      }
    };

    const annotArray = [providerSignature, timestampMessage];
    const annots = await page.addAnnotGroup(annotArray, 0);
    annots.forEach((a) => {
      const component = pageRender.getAnnotRender(a.getUniqueID())?.getComponent();
      component?.active();
    });
  }

  async addDate(page: PDFPage, rect: PDFRect, pageRender: PDFPageRender): Promise<void> {
    const date = this.formatDate();
    const annotJson: AnnotJson = {
      color: 'ffffff',
      contents: date,
      'interior-color': 0x00000000,
      rect: {
        bottom: rect.bottom,
        left: rect.left,
        right: rect.right,
        top: rect.top,
      },
      rotate: page.getRotation(),
      subject: 'FreeTextTypeWriter',
      'text-color': 0x000000,
      title: 'DateSigned',
      type: 'freetext',
    };
    const annots = await page.addAnnot(annotJson);
    const annot = annots[0];
    const component = pageRender.getAnnotRender(annot.getUniqueID())?.getComponent();
    component?.active();
    this.isDirty = true;
  }

  addTextBox(page: PDFPage, rect: PDFRect, pageRender: PDFPageRender) {
    const annotJson: AnnotJson = {
      color: 'ffffff',
      defaultAppearance: {
        textColor: 0,
        textSize: 24,
      },
      'interior-color': 0x00000000,
      rect: {
        bottom: rect.bottom,
        left: rect.left,
        right: rect.right,
        top: rect.top,
      },
      rotate: page.getRotation(),
      subject: 'FreeTextTypeWriter',
      'text-color': 0x000000,
      type: 'freetext',
    };
    page.addAnnot(annotJson).then((annots) => {
      const annot = annots[0];
      const component = pageRender.getAnnotRender(annot.getUniqueID())?.getComponent();
      component?.active();
    });
    this.isDirty = true;
  }

  // Takes each attachment for the current message that has been modified (signed or dated), and saves it to the database
  batchSaveAttachments(): Observable<string[]> {
    const obv: Array<Observable<string>> = [];
    this.attachmentsToSave.forEach((value: string, key: string) => {
      const editedAttachment: EditAttachmentDto = {
        Data: value,
        AttachmentUid: key,
      };
      obv.push(this.attachmentRepository.setAttachmentContent((editedAttachment.AttachmentUid).toString(), editedAttachment));
    });
    this.isDirty = false;
    this.attachmentsToSave.clear();
    return forkJoin(obv);
  }

  // If an attachment has been modified, we want to add it to our map (attachmentsToSave) to keep the modified data stored until we save
  // it to the database.
  attachmentModified(docId: string): Observable<boolean> {
    return this.getModifiedAttachmentData().pipe(
      switchMap((modifiedData: string) => {
        if (modifiedData) {
          this.attachmentsToSave.set(docId, modifiedData);
          this.isDirty = false;
          return of(true);
        } else {
          return throwError(new Error('Modified attachment data not retrieved'));
        }
      }),
    );
  }

  // To get the new attachment data that includes signatures/dates, we need to use several Foxit methods/objects to pull the final data.
  // The attachment must also be flattened, otherwise faxes and other attachment export functions will not include the signature/date,
  // despite the attachment displaying normally in PRM.
  getModifiedAttachmentData(): Observable<string> {
    const promise = new Promise<string>((resolve, reject) => {
      this.pdfUI.getCurrentPDFDoc().then((pdfDoc) => {
        if (!pdfDoc) {
          reject();
          return;
        }

        pdfDoc.flatten(0).then(() => {
          this.pdfDocumentStream(pdfDoc).then((pdfBlob) => {
            this.browserService.blobToBase64(pdfBlob).then((pdfBase64) => {
              if (pdfBase64 != null) {
                resolve(pdfBase64);
              } else {
                reject();
              }
            });
          });
        });
      });
    });
    return from(promise);
  }

  // Used when switching between attachments in a message
  checkIfAttachmentDirty(docId: string): Promise<void> {
    return new Promise<void>((resolve) => {
      if (this.isDirty === true) {
        return this.attachmentModified(docId).subscribe(() => resolve());
      }
      resolve();
    });
  }

  // Used when moving to the next message
  checkIfAttachmentGroupDirty(docId: string): Observable<string[]> {
    if (this.isDirty) {
      return of(docId).pipe(
        switchMap(() => this.attachmentModified(docId)),
        switchMap(() => this.batchSaveAttachments()),
      );
    } else if (this.attachmentsToSave.size > 0) {
      return of(docId).pipe(
        switchMap(() => this.batchSaveAttachments()),
      );
    } else {
      return of([]);
    }
  }

  getRect(
    startPoints: readonly [x: number, y: number],
    pageRect: DOMRect,
    endPoints: readonly [x: number, y: number],
  ): DeviceRect {
    let left = startPoints[0];
    let top = startPoints[1];
    let right = endPoints[0] - pageRect.left;
    let bottom = endPoints[1] - pageRect.top;
    let temp: number;
    if (right < left) {
      temp = left;
      left = right;
      right = temp;
    }
    if (bottom < top) {
      temp = top;
      top = bottom;
      bottom = temp;
    }
    return {
      left,
      top,
      right,
      bottom,
    };
  }

  formatDate(includeTime?: boolean): string {
    const formatString = 'MM/DD/YYYY' + ((includeTime) ? ' hh:mm A' : '');
    return moment().format(formatString);
  }

  adjustZoom() {
    this.pdfUI.pdfViewer.zoomTo('fitHeight').then(() => {
      this.pdfUI.pdfViewer.zoomTo('fitWidth');
    });
  }
}
